import { useDispatch, useSelector } from 'react-redux';
import Modal from 'components/Modal/Modal';
import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { setDialogState } from 'reduxStore/reducers/dialogSlice';
import LoginChildren from './component/Login';
import {
  emailRegExp,
  email_error,
  login,
  login_text1,
  login_text4,
  password_error,
  first_name_error,
  last_name_error,
  signup,
} from './constants/text';
import { loginUser, registerUser } from 'reduxStore/actions/auth';
import { getProfileDetails } from 'reduxStore/actions/user';
import { sign_of_ovulation } from 'pages/Family/OvulationTracker/constants/text';

const Login = () => {
  const { dialogOpenState } = useSelector((state) => state.dialogOpenState);
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [passwordHide, setPasswordHide] = useState(true);
  const isLogin = !dialogOpenState.register;

  const loginValidationSchema = Yup.object({
    email: Yup.string()
      .transform((value) => (value.trim() === '' ? '' : value.trim()))
      .required(email_error.require_email)
      .min(10, 'Email must be at least 10 characters long')
      .max(30, 'Email must be less than 30 characters long')
      .matches(emailRegExp, email_error.valid_email)
      .matches(
        /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
        'Email cannot contain capital letters'
      ).matches(
      /^[\w.-]+@[^0-9][\w.-]+\.[a-zA-Z]{2,}$/,
      "Email cannot have a number after @"
    ),

    password: Yup.string()
      .required(password_error)
      .trim()
      .min(8, 'Password must be at least 8 characters long')
      .max(15, 'Password must be less than 15 characters long')

      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(
        /[@$!%*?&#]/,
        'Password must contain at least one special character (@, $, !, %, *, ?, &)'
      ),
  });
  const signupValidationSchema = Yup.object({
    email: Yup.string()
      .transform((value) => (value.trim() === '' ? '' : value.trim()))
      .required(email_error.require_email)
      .min(10, 'Email must be at least 10 characters long')
      .max(30, 'Email must be less than 30 characters long')
      .matches(emailRegExp, email_error.valid_email)
      .matches(
        /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
        'Email cannot contain capital letters'
      ).matches(
      /^[\w.-]+@[^0-9][\w.-]+\.[a-zA-Z]{2,}$/,
      "Email cannot have a number after @"
    )
,
    password: Yup.string()
      .required(password_error)
      .trim()
      .min(8, 'Password must be at least 8 characters long')
      .max(15, 'Password must be less than 15 characters long')

      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(
        /[@$!%*?&#]/,
        'Password must contain at least one special character (@, $, !, %, *, ?, &)'
      ),

    first_name: Yup.string()
      .required(first_name_error)

      .min(2, 'First name must be at least 2 characters')
      .max(20, 'First name cannot be more than 20 characters')
      .matches(
        /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
        'Name can only contain alphabet letters.'
      ),

    last_name: Yup.string()
      .required(last_name_error)

      .min(2, 'Last name must be at least 2 characters')
      .max(20, 'First name cannot be more than 20 characters')
      .matches(
        /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
        'Name can only contain alphabet letters.'
      ),

    country: Yup.string().required('Country is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      country: '',
    },
    validationSchema: isLogin ? loginValidationSchema : signupValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log('Form submitted with values:', values);
      console.log('isLogin:', isLogin);
      onSubmitAPICall();
    },
  });

  formikRef.current = formik;

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    setPasswordHide(true);
  }, [isLogin]);

  const onSubmitAPICall = () => {
    if (isLogin) {
      dispatch(
        loginUser({
          email: formik.values.email,
          password: formik.values.password,
        })
      )
        .unwrap()
        .then((response) => {
          toast.success(response?.message || 'Login successful');

          dispatch(getProfileDetails())
            .unwrap()
            .then(() => {
              dispatch(
                setDialogState({
                  Login: false,
                })
              );
              window.location.reload();
            });
        })
        .catch((error) => {
          console.log('🚀 ~ onSubmitAPICall ~ error:', error);
          if (error?.requires_verification) {
            localStorage.setItem('signupEmail', formik.values.email);

            dispatch(
              setDialogState({
                ...dialogOpenState,
                logIn: false,
                isLoginOtp: true,
                loginOtpEmail: error.email,
              })
            );
          }
        });
    } else {
      dispatch(
        registerUser({
          email: formik.values.email,
          password: formik.values.password,
          first_name: formik.values.first_name,
          last_name: formik.values.last_name,
          country: formik.values.country,
        })
      )
        .unwrap()
        .then((registerResponse) => {
          if (registerResponse?.success) {
            localStorage.setItem('signupEmail', formik.values.email);
            dispatch(
              setDialogState({
                logIn: false,
                register: false,
                otp: true,
                isSignupOtp: true,
              })
            )
              .unwrap()
              .then(() => {
                window.location.reload();
              });
          }
        })
        .catch((error) => {});
    }
  };
  return (
    <Modal
      modalContentClassname={'sm:min-w-[490px] '}
      title={dialogOpenState.register ? signup : login}
      disableOutsideClick={true}
      closeHandler={() => {
        dispatch(
          setDialogState({
            ...dialogOpenState,
            logIn: false,
            register: false,
          })
        );
      }}
      text={dialogOpenState.register ? login_text1 : login_text4}
      children={
        <LoginChildren
          passwordHide={passwordHide}
          setPasswordHide={setPasswordHide}
          isLogin={!dialogOpenState.register}
          formik={formik}
        />
      }
    />
  );
};

export default Login;
